import { useMemo } from 'react';

import BackendGateway from '@shared/data-access-frontend/backend-gateway';
import EventSource from '@shared/data-access-frontend/event-source';
import UBFFGateway from '@shared/data-access-frontend/ubff-gateway';
import logger from '@shared/utils-frontend/logger';
import MiniEventTracker from '@shared/utils-frontend/mini-events/mini-event-tracker';
import buildObserverClient from '@shared/utils-frontend/observer-client/build-observer-client';
import I18nService from '@shared/utils-universal/i18n/i18n-service';
import { getPageName } from '@shared/utils-universal/page-name';
import {
  getUtmSource,
  trafficSource as getTrafficSource,
} from '@shared/utils-universal/traffic-source';
import { useLocation } from 'react-router-dom';

import { useStaticMapsUrls } from './useStaticMapsUrls';

import type { Logging } from '@shared/types/client-side-params';
import type { I18nServiceOptions } from '@shared/types/i18n/i18n-service';
import type { UserContext } from '@shared/types/user-context';
import type { Environment } from '@shared/types/utils';

type Props = {
  userContext: UserContext;
  i18n: I18nServiceOptions;
  clientEnv: Environment;
  backendUrl: string;
  googleMapsApiKey: string;
  eventSourcePolyfillEnabled: boolean;
  correlationId: string;
};

export const useContextValues = ({
  backendUrl,
  clientEnv,
  correlationId,
  eventSourcePolyfillEnabled,
  googleMapsApiKey,
  i18n,
  userContext,
}: Props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const staticMapsUrls = useStaticMapsUrls();

  /*
   * In React Router v7, any client-side navigation — including hash changes can cause the root-level App component to re-render.
   *
   * Although we use `shouldRevalidateProvider` to prevent unnecessary loader re-execution, such re-renders would still recreate all context values
   * (e.g. `uBFFGateway`, `BackendGateway`, `logging`, etc.).
   *
   * Recreating these instances on each re-render can lead to unexpected downstream effects: components or hooks
   * that depend on them (through React Context) would also re-run or re-rendered.
   *
   * To prevent this, we cache these service instances for the entire lifecycle of a single page load,
   * ensuring that navigation does not cause unintended instance changes.
   */
  return useMemo(() => {
    const i18nService = I18nService(i18n, logger);

    const observerClient = buildObserverClient({
      environment: clientEnv,
    });

    const source = searchParams.get('source');
    const utmSource = searchParams.get('utm_source');
    const trafficSource = getTrafficSource({ source });
    const {
      device,
      enableSEOVariantHotelsDetailsPage,
      impressionId,
      isWebView,
      pageType,
      searchCycleId,
    } = userContext;

    const logging: Logging = {
      correlationId,
      pageName: getPageName(pageType, enableSEOVariantHotelsDetailsPage),
      locale: i18n.culture.locale,
      searchCycleId,
      impressionId,
      isWebView: `${isWebView}`,
      isRobot: `${device.isRobot}`,
    };

    const miniEventTracker = MiniEventTracker({
      observerClient,
      trafficSource,
      utmSource: utmSource ? getUtmSource(utmSource) : null,
      device,
      isWebView,
      pageType,
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
      utmTerm: searchParams.get('utm_term'),
      impressionId,
    });

    const eventSource = EventSource(logger, eventSourcePolyfillEnabled);

    const backendGateway = BackendGateway({
      backendUrl,
      eventSource,
      i18n: i18nService,
      userContext,
    });

    const uBFFGateway = UBFFGateway({
      userContext,
    });

    const googleMaps = {
      apiKey: googleMapsApiKey,
      staticMapsUrls,
    };

    return {
      i18nService,
      miniEventTracker,
      backendGateway,
      uBFFGateway,
      googleMaps,
      logging,
    };
    // Cache for the entire page lifecycle — never re-run within a single load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
